
import { getAuth } from "firebase/auth";
import { collection, deleteDoc, doc, getDocs, getFirestore, onSnapshot, query, setDoc, where } from 'firebase/firestore';
import { Calendar } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { app } from "../../firebase";
import { useToast } from '../../hooks/use-toast';
import { storage } from '../../lib/storage';
import { Batch, ClassSchedule } from '../../types/common';

const Schedules = () => {
  const { toast } = useToast();
  const [selectedBatch, setSelectedBatch] = useState<string>("");
  const [employees, setEmployees] = useState([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [schedules, setSchedules] = useState<any[]>([]);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<any | null>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>("");
  const [selectedDay, setSelectedDay] = useState<string>("");
  const [classScheduleBatches, setClassScheudleBatches] = useState([]);
  const [subjects, setSubjects] = useState([]);


  const db = getFirestore(app);
  const auth = getAuth(app);

  const timeSlots = [
    "09:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
    "01:00 PM", "02:00 PM", "03:00 PM", "04:00 PM",
    "05:00 PM", "06:00 PM"
  ];

  const days = [
    "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY"
  ];


  useEffect(() => {
    // Load batches
    const unsubscribe = onSnapshot(
      collection(db, "courseBatches"),
      (querySnapshot) => {
        const result: any = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })).sort(sortByBatchName).filter((x: any) => x.isActive);

        setBatches(result);
        setSelectedBatch(result[0].id)
      },
      (error) => {
        toast({ title: error.message, description: "" });
      }
    );

    const facultyUnsubscribe = onSnapshot(
      collection(db, "employees"),
      (querySnapshot) => {
        const result: any = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })).sort(sortByEmployeesName).filter((x: any) => x.isActive);
        setEmployees(result);
      },
      (error) => {
        toast({ title: error.message, description: "" });
      }
    );

    const subjectsUnsubscribe = onSnapshot(
      collection(db, "allSubjects"),
      (querySnapshot) => {
        const result: any = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setSubjects(result);
      },
      (error) => {
        toast({ title: error.message, description: "" });
      }
    );
    return () => {
      unsubscribe();
      facultyUnsubscribe();
      subjectsUnsubscribe();
    }
  }, []);


  const fetchClassScheduleBatches = useCallback(async () => {
    try {
      const batchesRef = collection(db, "classScheduleBatches");
      const q = query(batchesRef, where("batchId", "==", selectedBatch));
      const querySnapshot = await getDocs(q);
      const result = querySnapshot.docs.map((doc) => ({
        id: doc.id, // This is the correct way to get the document ID
        ...doc.data(),
      }));
      setClassScheudleBatches(result);
    } catch (error) {
      toast({ title: error.message, description: "" });
    }
  }, [selectedBatch]); // Dependency on selectedBatch


  function sortByBatchName(x: any, y: any) {
    if (x.batchName > y.batchName) return 1;
    else if (x.batchName < y.batchName) return -1;
    else return 0;
  }

  function sortByEmployeesName(x: any, y: any) {
    if (x.employeesName > y.employeesName) return 1;
    else if (x.employeesName < y.employeesName) return -1;
    else return 0;
  }



  useEffect(() => {
    if (selectedBatch) {
      fetchClassScheduleBatches();
    }
  }, [selectedBatch]);




  const handleBatchChange = (value: string) => {
    setSelectedBatch(value);
  };

  const handleAddSchedule = (weekday: string, timeSlot: string) => {
    setSelectedDay(weekday);
    setSelectedTimeSlot(timeSlot);
    setEditingSchedule({
      id: "",
      batchId: selectedBatch,
      batchName: batches.find(b => b.id === selectedBatch)?.batchName || "",
      subject: "",
      instructor: "",
      weekday: weekday as any,
      startTime: timeSlot,
      endTime: getEndTime(timeSlot)
    });
    setIsEditDialogOpen(true);
  };

  const getEndTime = (startTime: string): string => {
    // Simple calculation - just add 1.5 hours
    const hour = parseInt(startTime.split(':')[0]);
    const isPM = startTime.includes('PM');

    let newHour = hour + 1;
    if (newHour > 12) newHour = newHour - 12;

    return `${newHour}:30 ${isPM ? 'PM' : 'AM'}`;
  };

  const handleEditSchedule = (schedule: ClassSchedule) => {
    setSelectedDay(schedule.weekday);
    setSelectedTimeSlot(schedule.timing);
    setEditingSchedule(schedule);
    setIsEditDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsEditDialogOpen(false);
    setEditingSchedule(null);
  };

  const handleSaveSchedule = async () => {
    if (!editingSchedule || !editingSchedule.classDetails || !editingSchedule.instructor) {
      toast({
        title: "Error",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    if (editingSchedule.id) {

      // Update the existing document in Firestore  
      const courseRef = doc(db, "classScheduleBatches", editingSchedule?.id); // Get a reference to the course document  

      // Use setDoc to update the document in Firestore  
      await setDoc(courseRef, editingSchedule); // Update the course data in Firestore  
      toast({
        title: "Schedule Updated",
        description: "class schedule has been updated successfully.",
        className: "bg-bytesplash-blue text-white",
      });
    } else {
      // Add new schedule
      // Add the generated ID to the course data object  

      // 1. Get a reference to a new document with an auto-generated ID
      const scheduleRef = doc(collection(db, "classScheduleBatches"));

      // 2. The ID is available on the 'scheduleRef' object
      const newId = scheduleRef.id;
      // 3. Prepare your data, including the ID
      const scheudleWithId = {
        ...editingSchedule, // Spread the existing data (e.g., batchId, className, etc.)
        id: newId, // Assign the generated ID
      };
      // Save the course data to Firestore with the generated ID
      await setDoc(scheduleRef, scheudleWithId);

      toast({
        title: "Schedule created",
        description: `Schedule has been successfully created`,
      });

    }
      fetchClassScheduleBatches();
    handleCloseDialog();
  };

  // Delete schedule logic
  const handleDeleteSchedule = async () => {
    if (!editingSchedule?.id) return;
    try {
      await deleteDoc(doc(db, "classScheduleBatches", editingSchedule?.id));
      // If deleteDoc is not available, use the following line
      setEditingSchedule(null);
      setIsEditDialogOpen(false);
      toast({
        title: "Schedule Deleted",
        description: "Class schedule has been deleted successfully.",
        className: "bg-bytesplash-blue text-white",
      });
      fetchClassScheduleBatches();
      handleCloseDialog();
    } catch (error) {
      toast({ title: error.message, description: "", variant: "destructive" });
    }
  };

  const getScheduleForCell = (weekday: string, timeSlot: string): ClassSchedule | undefined => {
    return classScheduleBatches.find(s => s.weekday.toLowerCase() === weekday.toLowerCase() && s.timing === timeSlot);
  };

  const selectedBatchName = batches.find(b => b.id === selectedBatch)?.batchName || "";

  return (
    <div className="p-6">


      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <div className="w-full md:w-72">
          <Select value={selectedBatch} onValueChange={handleBatchChange}>
            <SelectTrigger className="border-2 hover:border-bytesplash-blue">
              <SelectValue placeholder="Select Course" />
            </SelectTrigger>
            <SelectContent>
              {batches.map((batch) => (
                <SelectItem key={batch.id} value={batch.id}>
                  {batch.batchName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <h1 className="text-2xl font-bold text-center text-bytesplash-blue">
          Class Schedule Of Selected Batch
        </h1>
      </div>

      <Card className="shadow-lg border-t-2 border-t-bytesplash-blue">
        {editingSchedule?.id && (
          <Button
            variant="destructive"
            onClick={handleDeleteSchedule}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            Delete
          </Button>
        )}
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="w-24 p-4 border text-center text-sm"></th>
                  {days.map((weekday) => (
                    <th key={weekday} className="p-4 border text-center text-bytesplash-blue font-semibold text-md">
                      {weekday}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {timeSlots.map((timeSlot) => (
                  <tr key={timeSlot}>
                    <td className="p-4 border text-center">
                      <span className="text-bytesplash-blue font-semibold text-sm">{timeSlot}</span>
                    </td>
                    {days.map((weekday) => {
                      const schedule = getScheduleForCell(weekday, timeSlot);

                      return (
                        <td
                          key={`${weekday}-${timeSlot}`}
                          className={`p-4 border ${schedule ? 'bg-bytesplash-soft-blue' : ''} cursor-pointer hover:bg-gray-50 transition-colors rounded-md ${schedule ? 'rounded-tl-[1rem] rounded-bl-[1rem]' : ''}`}
                          onClick={() => schedule ? handleEditSchedule(schedule) : handleAddSchedule(weekday, timeSlot)}
                        >
                          {schedule && (
                            <div className="text-center">
                              <div className="font-semibold text-bytesplash-blue text-sm">{schedule.classDetails}</div>
                              <div className="text-sm text-gray-600">by - {schedule.instructor}</div>
                              <p className="text-xs text-gray-400">{schedule.currentTopic}</p>
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Schedule Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-bytesplash-blue" />
              <span>{editingSchedule?.id ? "Edit" : "Add"} Class Schedule</span>
            </DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="batch" className="text-right">Batch*</Label>
              <Select
                value={editingSchedule?.batchId || ""}
                onValueChange={(value) => setEditingSchedule(prev => prev ? { ...prev, batchId: value } : null)}
                disabled
              >
                <SelectTrigger id="batch" className="col-span-3 border-2 hover:border-bytesplash-blue">
                  <SelectValue placeholder="Select batch" />
                </SelectTrigger>
                <SelectContent>
                  {batches.map((batch) => (
                    <SelectItem key={batch.id} value={batch.id}>
                      {batch.batchName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="instructor" className="text-right">Instructor*</Label>
              <Select
                value={editingSchedule?.instructor || ""}
                onValueChange={(value) => setEditingSchedule(prev => prev ? { ...prev, instructor: value } : null)}
              >
                <SelectTrigger id="instructor" className="col-span-3 border-2 hover:border-bytesplash-blue">
                  <SelectValue placeholder="Select instructor" />
                </SelectTrigger>
                <SelectContent>
                  {
                    employees?.map((e: any) => {
                      return <>
                        <SelectItem value={e.employeesName}>{e.employeesName}</SelectItem>
                      </>
                    })
                  }
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="classDetails" className="text-right">Class Details*</Label>
              <Select
                value={editingSchedule?.classDetails || ""}
                onValueChange={(value) => setEditingSchedule(prev => prev ? { ...prev, classDetails: value } : null)}
              >
                <SelectTrigger id="classDetails" className="col-span-3 border-2 hover:border-bytesplash-blue">
                  <SelectValue placeholder="Select subject" />
                </SelectTrigger>
                <SelectContent>
                  {subjects?.map((subject: any, i: number) => (
                    <SelectItem key={subject.className + i + 1} value={subject.className}>
                      {subject.className}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="timing" className="text-right">Timing*</Label>
              <Select
                value={editingSchedule?.timing || ""}
                onValueChange={(value) => setEditingSchedule(prev => prev ? {
                  ...prev,
                  timing: value,
                  endTime: getEndTime(value)
                } : null)}
              >
                <SelectTrigger id="timing" className="col-span-3 border-2 hover:border-bytesplash-blue">
                  <SelectValue placeholder="Select time" />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map((slot) => (
                    <SelectItem key={slot} value={slot}>
                      {slot}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="weekday" className="text-right">Weekday*</Label>
              <Select
                value={selectedDay}
                onValueChange={(value) => {
                  setSelectedDay(value);
                  setEditingSchedule(prev => prev ? { ...prev, weekday: value as any } : null);
                }}
              >
                <SelectTrigger id="weekday" className="col-span-3 border-2 hover:border-bytesplash-blue">
                  <SelectValue placeholder="Select day" />
                </SelectTrigger>
                <SelectContent>
                  {days.map((day) => (
                    <SelectItem key={day} value={day}>
                      {day.toUpperCase()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="currentTopic" className="text-right">Current Topic</Label>
              <Textarea
                id="currentTopic"
                className="col-span-3 border-2 hover:border-bytesplash-blue"
                value={editingSchedule?.currentTopic || ""}
                onChange={(e) => setEditingSchedule(prev => prev ? { ...prev, currentTopic: e.target.value } : null)}
                placeholder="Enter the current topic being covered"
              />
            </div>
          </div>

          <DialogFooter className="sm:justify-between">
            <Button
              variant="outline"
              onClick={handleCloseDialog}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveSchedule}
              className="bg-bytesplash-blue hover:bg-blue-700"
            >
              Save & Submit
            </Button>
            {editingSchedule?.id && (
              <Button
                variant="destructive"
                onClick={handleDeleteSchedule}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Delete
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Schedules;
